import { useRef, useState } from "react";
import { Loader2, RefreshCcw, Copy, X, GitMerge } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";

export function UniverseCleaningTools({ onChanged }) {
  const syncRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dups, setDups] = useState(null);
  const [dupsLoading, setDupsLoading] = useState(false);
  const [survivor, setSurvivor] = useState({});
  const [merging, setMerging] = useState(null);

  const runSync = async (file, apply) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/master-data/sync-cleaned-excel?apply=${apply}`, fd,
      { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
    return data;
  };

  const onSyncFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const p = await runSync(f, 0);
      setPreview(p);
      setPendingFile(f);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const applySync = async () => {
    setBusy(true);
    try {
      const r = await runSync(pendingFile, 1);
      toast.success(`Universe synced — ${r.updates} updated, ${r.creates} added, ${r.deletes} removed. Now ${r.total_universe} companies.`, { duration: 8000 });
      setPreview(null); setPendingFile(null);
      onChanged?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const findDups = async () => {
    setDupsLoading(true);
    try {
      const { data } = await api.get("/master-data/duplicate-finder");
      setDups(data);
      if (!data.groups.length) toast.success("No near-duplicate company names found — Universe is clean!");
    } catch (err) { toast.error(apiError(err)); } finally { setDupsLoading(false); }
  };

  const mergeGroup = async (gi, group) => {
    const surv = survivor[gi] || group[0].id;
    setMerging(gi);
    try {
      const { data } = await api.post("/master-data/merge-companies",
        { survivor_id: surv, merge_ids: group.map((x) => x.id).filter((id) => id !== surv) });
      toast.success(`Merged ${data.merged} duplicates into ${data.survivor}`);
      setDups((p) => ({ ...p, groups: p.groups.filter((_, i) => i !== gi), total_groups: p.total_groups - 1 }));
      onChanged?.();
    } catch (err) { toast.error(apiError(err)); } finally { setMerging(null); }
  };

  return (
    <>
      <input ref={syncRef} type="file" accept=".xlsx" className="hidden" onChange={onSyncFile} data-testid="sync-cleaned-input" />
      <Button variant="outline" className="gap-1.5" onClick={() => syncRef.current?.click()} disabled={busy} data-testid="sync-cleaned-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />} Sync Cleaned
      </Button>
      <Button variant="outline" className="gap-1.5" onClick={() => (dups ? setDups(null) : findDups())} disabled={dupsLoading} data-testid="find-duplicates-btn">
        {dupsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} {dups ? "Hide Duplicates" : "Find Duplicates"}
      </Button>

      <AlertDialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <AlertDialogContent data-testid="sync-preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apply cleaned Universe sheet?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm space-y-2">
                <p><b className="text-slate-900">{preview?.updates}</b> companies will be updated · <b className="text-emerald-700">{preview?.creates}</b> new added · <b className="text-red-600">{preview?.deletes}</b> removed (missing from your sheet)</p>
                {preview?.skipped_in_crm_deletes > 0 && <p className="text-amber-600">{preview.skipped_in_crm_deletes} in-CRM companies missing from the sheet will be KEPT (linked to leads/deals).</p>}
                {preview?.sample_deletes?.length > 0 && <p className="text-xs text-slate-500">Removing e.g.: {preview.sample_deletes.slice(0, 5).join(", ")}…</p>}
                {preview?.errors_count > 0 && <p className="text-red-600 text-xs">{preview.errors_count} row errors will be skipped.</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sync-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-slate-900 hover:bg-slate-800 gap-2" disabled={busy} data-testid="sync-apply"
              onClick={(e) => { e.preventDefault(); applySync(); }}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {dups && dups.groups.length > 0 && (
        <Card className="p-5 w-full order-last" data-testid="duplicates-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-heading font-semibold text-slate-900">Possible duplicate companies · {dups.total_groups} groups</p>
              <p className="text-xs text-slate-500">Pick the record to keep, then Merge — contacts, leads and deals move to it automatically.</p>
            </div>
            <button onClick={() => setDups(null)} className="text-slate-400 hover:text-slate-700" data-testid="duplicates-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {dups.groups.map((g, gi) => (
              <div key={gi} className="border border-slate-200 rounded-lg p-3" data-testid={`dup-group-${gi}`}>
                <div className="space-y-1.5">
                  {g.map((row) => (
                    <label key={row.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name={`surv-${gi}`} checked={(survivor[gi] || g[0].id) === row.id}
                        onChange={() => setSurvivor((p) => ({ ...p, [gi]: row.id }))} data-testid={`dup-pick-${row.id}`} />
                      <span className="font-medium text-slate-800">{row.company}</span>
                      <span className="text-xs text-slate-400">{row.location || "—"} · {row.contacts} contacts{row.status === "in_crm" ? " · in CRM" : ""}</span>
                    </label>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="mt-2 h-7 gap-1.5 text-xs" disabled={merging === gi}
                  onClick={() => mergeGroup(gi, g)} data-testid={`dup-merge-${gi}`}>
                  {merging === gi ? <Loader2 className="h-3 w-3 animate-spin" /> : <GitMerge className="h-3 w-3" />} Merge into selected
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}
